import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom } from 'rxjs';
import { EditLinks } from '../../../../../@core/constants/edit-links';
import { BaseListCombinedService } from '../../../../../@core/services/base-list-combined.service';
import { Address, AddressContactLink, DeletionResult, Operator } from '../../../../../generated/generated-types';
import { WidgetCrmAddressListNewComponent } from '../../../components/widget-crm-address-list-new/widget-crm-address-list.component';
import { AddressContactLinkService } from '../../../services/address-contact-link.service';
import { ContactService } from '../../../services/contact.service';

type ContactLinkIdType = { href: string, label: string, addressId: string, addressIdentifier: string, contactId: string, contactlinkId: string, temporary?: boolean };

@Component({
  selector: 'app-address-contact-link-add-and-list',
  templateUrl: './address-contact-link-add-and-list.component.html',
  styleUrl: './address-contact-link-add-and-list.component.scss',
  imports: [ButtonModule, CommonModule, RouterModule, TooltipModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressContactLinkAddAndListComponent extends FieldType {

  contactAddressLink: AddressContactLink;
  selectedAddress: string;
  contactLinkedAddressListSignal = signal<ContactLinkIdType[]>([]);
  contactLinkedAddressList: ContactLinkIdType[] = [];
  isAnyContactAddressLinkTemporary = computed(() => this.contactLinkedAddressListSignal().some(address => address.temporary));
  addressContactLinkResult = signal<AddressContactLink[]>([]);
  destroyRef = inject(DestroyRef);


  constructor(
    private baseServices: BaseListCombinedService,
    private addressContactLinkService: AddressContactLinkService,
    private contactService: ContactService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  async ngAfterViewInit() {
    await this.getAllAddressesLinkedToContact();

    this.form.controls['contactId'].valueChanges?.pipe(takeUntilDestroyed(this.destroyRef))?.subscribe(async () => {
      this.contactLinkedAddressList = [];
      this.contactLinkedAddressListSignal.set(this.contactLinkedAddressList);
      await this.getAllAddressesLinkedToContact();
    });

    if (this.options.formState.detailComponent.detailModel?.address) {
      if (this.contactLinkedAddressListSignal().length == 0) {
        //just set for new from inside address edit mode
        this.contactLinkedAddressList.push({ href: `${EditLinks.ADDRESS}/${this.options.formState.detailComponent.detailModel?.address?.id}`, label: `${this.options.formState.detailComponent.detailModel?.address?.name}`, addressId: this.options.formState.detailComponent.detailModel?.address?.id, contactId: this.options.formState.detailComponent.detailModel?.contact?.id, contactlinkId: null, addressIdentifier: this.options.formState.detailComponent.detailModel?.address?.name, temporary: true });
        this.contactLinkedAddressListSignal.set(this.contactLinkedAddressList);
        this.cdRef.detectChanges();
      }
    }
  }

  async dirtyCheckParentAndSave() {
    const detailComponent = this.formState.detailComponent;
    if (this.formState?.isDirty && detailComponent?.detailModel) {
      await detailComponent.save(detailComponent.detailModel, null, { noCloseModal: true });
    }
  }

  /**
   * @description This method is called when the user clicks on the "Add Contact to Address" button!
   * @returns void
   */
  async setContactAddressLinkToAddress(): Promise<void> {
    await this.dirtyCheckParentAndSave();
    // Step 1: Display the modal to select an address
    const result = this.baseServices.modalService.fromComponent(WidgetCrmAddressListNewComponent, {
      closable: true,
      size: 'lg',
      title: this.baseServices.i18nService.translate(_("common.dialog-Associate-contact-to-address")),
      locals: {
        tableSelectionMode: "single"
      }
    });
    const sub = result.subscribe(async (resArray: Address[]) => {
      if (resArray?.length > 0) {
        let res = resArray[0];
        // Check if the address is already linked to the contact
        const result = this.contactLinkedAddressListSignal().find((item) => item.addressId == res.id);
        // Setp 2: Save the address-contact link
        if (result) {
          // Step 3: Check if the address-contact link was saved successfully
          const msgAlreadySaved = this.baseServices.i18nService.translate(_('common.notify-save-duplicated'), { entry: `${res.name}` });
          this.baseServices.notificationService.warning(msgAlreadySaved);
          return;
        }
        const savedResult = await this.addressContactLinkService.saveItem({
          addressId: res.id,
          contactId: this.model.contactId ?? this.model.contact?.id,
        });
        console.log("savedResult", savedResult);
        if (!savedResult) return;
        // Notify user
        const msgSavedSuccess = this.baseServices.i18nService.translate(_('common.save-successful'), { entry: `${res.name}` });
        this.baseServices.notificationService.success(msgSavedSuccess);

        // Step 4: Add the address to the list of linked addresses
        const addressIdentifier = res.name ? res.name : (res.firstName + " " + res.lastName).trim();
        this.contactLinkedAddressList.push({ href: `${EditLinks.ADDRESS}/${res.id}`, label: `${res.name}`, addressId: res.id, contactId: this.model.contact?.id, contactlinkId: savedResult.id, addressIdentifier });
        this.contactLinkedAddressListSignal.set(this.contactLinkedAddressList);
        this.cdRef.detectChanges();
      }
      // Step 5: Unsubscribe from the observable
      sub.unsubscribe();
    });
  }


  async saveContactAndAddress(link: AddressContactLink) {

    if (this.form.invalid) {
      this.baseServices.notificationService.error(_("error.invalid-form"));
      return;
    }
    let saveLink = simpleDeepClone(link);
    let savedContact = await this.contactService.saveItem(saveLink.contact);

    saveLink.addressId = this.options.formState.detailComponent.addressId;
    saveLink.contactId = savedContact.id;
    let savedLink: AddressContactLink = saveLink;
    if (!this.options.formState.detailComponent.contactId)
      savedLink = await this.addressContactLinkService.saveItem(saveLink);
    // this.detailForm.reset();
    // this.cdRef.detectChanges();

    // this.model = {} as AddressContactLink;
    this.form.reset();
    this.cdRef.detectChanges();
    this.options.formState.detailComponent.onSavedCustom.emit(savedLink);
  }

  endEdit() {
    this.options.formState.detailComponent.onEndEditing.emit(true);
  }

  /**
   * @description Delete the contact and address link
   * @param link The link to delete
   */
  deleteContactAndAddress(link: AddressContactLink): void {
    this.addressContactLinkService.deleteItem(link.id).then(() => {
      this.cdRef.detectChanges();
      this.options.formState.detailComponent.onDeletedCustom.emit(link);
    });
  }

  deleteContactAddressLink(address: ContactLinkIdType): void {

    this.baseServices.modalService.showDeletionDialogOfSpecificItem(address.addressIdentifier).subscribe(async result => {
      if (result) {
        // Search in to this.contactLinkedAddressListSignal to find the addressId
        const contactlinkId = this.contactLinkedAddressListSignal().find((item) => item.contactlinkId == address.contactlinkId).contactlinkId;
        const deletedResult = await this.addressContactLinkService.deleteItem(contactlinkId);
        if (deletedResult.result == DeletionResult.DELETED) {
          const msgDeletedSuccess = this.baseServices.i18nService.translate(_('common.notify-delete-success'), { ModelName: `${this.model.firstName} ${this.model.lastName}` });
          this.baseServices.notificationService.success(msgDeletedSuccess);
          // Remove the address from the list of linked addresses
          this.contactLinkedAddressList = this.contactLinkedAddressListSignal().filter((item) => item.contactlinkId != address.contactlinkId);
          this.contactLinkedAddressListSignal.set(this.contactLinkedAddressList);
          return;
        } else {
          this.baseServices.notificationService.error(deletedResult.message);
          return;
        }
      } else {
        return
      }
    });
  }

  // Get all addresses linked to this contact
  async getAllAddressesLinkedToContact(): Promise<void> {
    // includes Address in GraphQL query and so updates cache probably!
    const addressContactLinkResult = await lastValueFrom(this.addressContactLinkService.getItems({
      searchFilter: {
        field: "contactId",
        operator: Operator.equal,
        value: this.model.contactId ?? this.model.contact?.id
      }
    }).mapSingle((res) => {
      return res.getAddressContactLinks.items;
    }));
    // this.addressContactLinkResult.set(addressContactLinkResult as AddressContactLink[]);
    for (let i = 0; i < addressContactLinkResult.length; i++) {
      let addressIdentifier = "";
      if (addressContactLinkResult[i].address)
        addressIdentifier = addressContactLinkResult[i].address.name ? addressContactLinkResult[i].address.name : (addressContactLinkResult[i].address.firstName + " " + addressContactLinkResult[i].address.lastName).trim();
      this.contactLinkedAddressList.push({ href: `${EditLinks.ADDRESS}/${addressContactLinkResult[i].addressId}`, label: `${addressIdentifier}`, addressId: addressContactLinkResult[i].addressId, contactId: addressContactLinkResult[i].contactId, contactlinkId: addressContactLinkResult[i].id, addressIdentifier });
      this.contactLinkedAddressListSignal.set(this.contactLinkedAddressList);
    }
  }

}
